import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, CircularProgress, InputAdornment, Menu, MenuItem, Pagination, TextField, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import DeleteDialog from '../../../components/admin/common/CustomDeleteDialog';
import { showCustomComponentNotification, showErrorNotification } from '../../../components/admin/common/NotiService';
import OZDialog from '../../../components/admin/common/OZDialog';
import { PaginationRenderIcon } from '../../../components/admin/common/OZDataGrid/OZGridPagination';
import { bulkDeleteDispositions, getdisposition } from '../../../services/api-service';

const BULK_DELETE_MAX_DISPOSITIONS = 100;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const LIST_HEIGHT = 480;
const ROW_HEIGHT = 48;
// Approximate non-list vertical space (dialog top offset, title, search,
// count row, pagination and footer) reserved when fitting the list to the viewport.
const DIALOG_VERTICAL_CHROME = 430;

// The bulk delete endpoint responds with { job_id, status: "queued", message: "Job will be
// processed" } on success. Treat a job_id / queued status (or an "initiated"/"processed"
// message) as success.
const isAsyncActionSuccess = (resp) => {
    if (!resp) {
        return false;
    }
    if (resp?.job_id) {
        return true;
    }
    const status = `${resp?.status || resp?.Status || ''}`.toLowerCase();
    if (status === 'queued' || status === 'success' || status === 'in_progress') {
        return true;
    }
    const message = `${resp?.message || resp?.Message || ''}`.toLowerCase();
    return message.includes('initiated') || message.includes('processed');
};

// The disposition list API returns an envelope { Status, Message, Data: [...] };
// pull out the rows array defensively.
const extractDispositionRows = (resp) => {
    if (Array.isArray(resp)) {
        return resp;
    }
    const candidate = resp?.Data ?? resp?.data ?? resp?.dispositions ?? resp?.Dispositions ?? resp?.records ?? resp?.Records ?? resp?.result ?? resp?.Result;
    return Array.isArray(candidate) ? candidate : [];
};

// Normalise a raw row into { id, reason }, tolerating PascalCase / camelCase variants.
const normaliseDisposition = (row) => ({
    id: row?.id ?? row?.Id ?? row?.dispositionId ?? row?.DispositionId ?? row?.disposition_id,
    reason: row?.reason ?? row?.Reason ?? row?.dispositionReason ?? row?.name ?? row?.Name ?? '',
});

const BulkDeleteDispositionDialog = ({ open, onClose, onDeleted }) => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [dispositions, setDispositions] = useState([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [rowsPerPageMenuAnchor, setRowsPerPageMenuAnchor] = useState(null);
    const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);

    useEffect(() => {
        const handleResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let active = true;
        const fetchDispositions = async () => {
            setLoading(true);
            try {
                const resp = await getdisposition();
                if (!active) {
                    return;
                }
                const rows = extractDispositionRows(resp)
                    .map(normaliseDisposition)
                    .filter((d) => d.id !== undefined && d.id !== null && `${d.reason}`.length > 0);
                setDispositions(rows);
            } catch (e) {
                console.error('Failed to load dispositions', e);
                if (active) {
                    setDispositions([]);
                    showErrorNotification('Unable to load dispositions. Please try again later.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        fetchDispositions();
        return () => {
            active = false;
        };
    }, []);

    const selectedCount = Object.keys(selected).length;
    const capReached = selectedCount >= BULK_DELETE_MAX_DISPOSITIONS;

    const filteredDispositions = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        if (!searchText) {
            return dispositions;
        }
        return dispositions.filter((d) => `${d.reason}`.toLowerCase().includes(searchText));
    }, [dispositions, search]);

    const totalPages = Math.max(1, Math.ceil(filteredDispositions.length / rowsPerPage));

    const pageDispositions = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredDispositions.slice(start, start + rowsPerPage);
    }, [filteredDispositions, page, rowsPerPage]);

    const hasActiveFilter = useMemo(() => search.trim().length > 0, [search]);

    useEffect(() => {
        setPage(1);
    }, [search, rowsPerPage]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    // Clear action remains scoped to the current searched list.
    const filteredSelectedCount = useMemo(
        () => filteredDispositions.reduce((count, d) => (selected[d.id] ? count + 1 : count), 0),
        [filteredDispositions, selected]
    );
    // Select-all checkbox is scoped to the current paginated search page only.
    const pageSelectedCount = useMemo(
        () => pageDispositions.reduce((count, d) => (selected[d.id] ? count + 1 : count), 0),
        [pageDispositions, selected]
    );
    const allPageSelected = pageDispositions.length > 0 && pageSelectedCount === pageDispositions.length;
    const somePageSelected = pageSelectedCount > 0 && !allPageSelected;
    const clearableSelectedCount = hasActiveFilter ? filteredSelectedCount : selectedCount;

    const toggleSelectAllPage = () => {
        const next = { ...selected };
        if (allPageSelected) {
            // Clear only the selected items present on the current paginated page.
            pageDispositions.forEach((d) => { delete next[d.id]; });
            setSelected(next);
            return;
        }
        // Select every item on the current paginated page, respecting the max selection cap.
        let truncated = false;
        for (let i = 0; i < pageDispositions.length; i += 1) {
            const disposition = pageDispositions[i];
            if (next[disposition.id]) {
                continue;
            }
            if (Object.keys(next).length >= BULK_DELETE_MAX_DISPOSITIONS) {
                truncated = true;
                break;
            }
            next[disposition.id] = disposition;
        }
        setSelected(next);
        if (truncated) {
            showErrorNotification(`You can select a maximum of ${BULK_DELETE_MAX_DISPOSITIONS} dispositions at a time.`);
        }
    };

    const clearSelected = () => {
        if (!hasActiveFilter) {
            setSelected({});
            return;
        }
        const next = { ...selected };
        filteredDispositions.forEach((d) => { delete next[d.id]; });
        setSelected(next);
    };

    const clearAllSelected = () => {
        setSelected({});
    };

    const toggleDisposition = (disposition) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[disposition.id]) {
                delete next[disposition.id];
            } else {
                if (Object.keys(next).length >= BULK_DELETE_MAX_DISPOSITIONS) {
                    return prev;
                }
                next[disposition.id] = disposition;
            }
            return next;
        });
    };

    const handleClose = () => {
        if (deleting) {
            return;
        }
        onClose?.();
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = useCallback((event) => {
        setRowsPerPage(Number(event.target.value));
        setRowsPerPageMenuAnchor(null);
    }, []);

    const notifyDeleteSuccess = (message) => {
        showCustomComponentNotification({
            component: (
                <label style={{ fontSize: '14px', fontWeight: '400', color: '#212121' }}>
                    {message}
                    <br />
                    <span
                        style={{ textDecoration: 'none', cursor: 'pointer', color: '#3d8bf8', fontSize: '12px' }}
                        onClick={() => { navigate('/admin/reportStatusAdmin'); }}
                    >
                        Click here to Navigate to "Report Status" Page
                    </span>
                </label>
            ),
            variant: 'success',
        });
    };

    const handleDelete = async () => {
        const selectedList = Object.values(selected);
        if (selectedList.length === 0) {
            return;
        }
        try {
            setDeleting(true);
            const ids = selectedList.map((d) => d.id).filter((id) => id !== undefined && id !== null).join(',');
            const resp = await bulkDeleteDispositions({ dispositionIds: ids });
            if (isAsyncActionSuccess(resp)) {
                const backendMessage = resp?.message || resp?.Message || 'Delete request submitted';
                notifyDeleteSuccess(`${backendMessage} for ${selectedList.length} selected disposition(s)${resp?.job_id ? ` (job id ${resp.job_id})` : ''}.`);
                onDeleted?.();
                onClose?.();
            } else {
                showErrorNotification(resp?.message || resp?.Message || 'Unable to queue delete request');
            }
        } catch (e) {
            console.error(e);
            showErrorNotification('Unable to queue delete request');
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    const rowStyle = useMemo(
        () => ({ fontSize: '14px', fontWeight: 400, color: '#212121' }),
        []
    );

    // Keep the dialog within the viewport: cap the list to the space left after the
    // dialog's top offset, header, search, pagination and footer chrome.
    const availableListHeight = Math.max(ROW_HEIGHT * 3, viewportHeight - DIALOG_VERTICAL_CHROME);
    const listBoxHeight = Math.min(LIST_HEIGHT, availableListHeight);
    const listHeight = Math.min(listBoxHeight, Math.max(pageDispositions.length, 1) * ROW_HEIGHT);

    const renderRow = ({ index, style }) => {
        const disposition = pageDispositions[index];
        const isSelected = Boolean(selected[disposition.id]);
        const disabled = !isSelected && capReached;
        return (
            <div style={style} key={disposition.id}>
                <Tooltip title={disabled ? `You can select a maximum of ${BULK_DELETE_MAX_DISPOSITIONS} dispositions at a time.` : ''}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', borderBottom: '1px solid #F0F0F0', pr: '8px' }}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={() => toggleDisposition(disposition)}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography noWrap sx={rowStyle}>{disposition.reason}</Typography>
                        </Box>
                    </Box>
                </Tooltip>
            </div>
        );
    };

    const clearTooltip = hasActiveFilter
        ? `Clear ${clearableSelectedCount} selected disposition(s) from current search`
        : 'Clear all selected dispositions';

    return (
        <OZDialog
            open={open}
            title="Bulk Delete Dispositions"
            onClose={handleClose}
            hideActions
            maxWidth="md"
        >
            <Box sx={{ width: 'min(720px, 86vw)', maxWidth: '100%' }}>
                <Typography sx={{ fontSize: '12px', color: '#99A0A8', mb: '6px' }}>
                    Select the dispositions you want to delete.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Tooltip title={allPageSelected ? 'Clear selected (current page)' : 'Select all (current page)'}>
                        <span>
                            <Checkbox
                                size="small"
                                checked={allPageSelected}
                                indeterminate={somePageSelected}
                                disabled={loading || pageDispositions.length === 0}
                                onChange={toggleSelectAllPage}
                            />
                        </span>
                    </Tooltip>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by reason"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '18px', color: '#99A0A8' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '8px', fontSize: '14px' },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '12px', mb: '4px' }}>
                    <Typography sx={{ fontSize: '12px', color: '#536580' }}>
                        {filteredDispositions.length} disposition(s)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {clearableSelectedCount > 0 && (
                            <Tooltip title={clearTooltip}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={clearSelected}
                                    disabled={deleting}
                                    sx={{
                                        fontSize: '12px',
                                        color: '#3D8BF8',
                                        cursor: deleting ? 'not-allowed' : 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                        opacity: deleting ? 0.5 : 1,
                                    }}
                                >
                                    Clear{hasActiveFilter ? ' filtered' : ''} ({clearableSelectedCount})
                                </Typography>
                            </Tooltip>
                        )}
                        {selectedCount > 0 && clearableSelectedCount === 0 && (
                            <Tooltip title={`Clear all ${selectedCount} selected disposition(s)`}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={clearAllSelected}
                                    disabled={deleting}
                                    sx={{
                                        fontSize: '12px',
                                        color: '#E65B5C',
                                        cursor: deleting ? 'not-allowed' : 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                        opacity: deleting ? 0.5 : 1,
                                    }}
                                >
                                    Clear all ({selectedCount})
                                </Typography>
                            </Tooltip>
                        )}
                        <Typography sx={{ fontSize: '12px', color: capReached ? '#E65B5C' : '#536580' }}>
                            {selectedCount} / {BULK_DELETE_MAX_DISPOSITIONS} selected
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ border: '1px solid #E6E5E6', borderRadius: '8px', height: `${listBoxHeight}px`, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : filteredDispositions.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography sx={{ fontSize: '14px', color: '#99A0A8' }}>No dispositions found.</Typography>
                        </Box>
                    ) : (
                        <List height={listHeight} itemCount={pageDispositions.length} itemSize={ROW_HEIGHT} width="100%">
                            {renderRow}
                        </List>
                    )}
                </Box>

                {!loading && filteredDispositions.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: '8px',
                            flexWrap: 'wrap',
                            gap: '8px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => setRowsPerPageMenuAnchor(e.currentTarget)}
                                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: '#546881' }} />}
                                sx={{
                                    borderRadius: '12px',
                                    borderColor: '#546881',
                                    color: '#000000',
                                    fontSize: '12px',
                                    minWidth: '103px',
                                    minHeight: '35px',
                                    textTransform: 'none',
                                }}
                            >
                                <Typography component="span" color="#546881" fontSize="12px" fontWeight="700">
                                    {rowsPerPage}
                                </Typography>
                                <Typography component="span" color="#000000" fontSize="12px" fontWeight="400" ml={0.5}>
                                    / Page
                                </Typography>
                            </Button>
                            <Menu
                                anchorEl={rowsPerPageMenuAnchor}
                                open={Boolean(rowsPerPageMenuAnchor)}
                                onClose={() => setRowsPerPageMenuAnchor(null)}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            mt: 0.5,
                                            borderRadius: '12px',
                                            border: '1px solid #546881',
                                            minWidth: '110px',
                                        },
                                    },
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <MenuItem
                                        key={size}
                                        value={size}
                                        onClick={handlePageSizeChange}
                                        sx={{
                                            fontSize: '13px',
                                            color: '#546881',
                                            borderTop: size !== PAGE_SIZE_OPTIONS[0] ? '1px solid rgba(0,0,0,0.1)' : 'none',
                                        }}
                                    >
                                        {size}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Typography sx={{ fontSize: '12px', color: '#536580' }}>
                                Page {page} of {totalPages}
                            </Typography>
                        </Box>
                        {totalPages > 1 && (
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handleChangePage}
                                variant="outlined"
                                shape="circular"
                                size="small"
                                sx={{
                                    '& .MuiPaginationItem-previousNext': { color: '#3D8BF8', border: 'none' },
                                    '& .MuiPaginationItem-previousNext.Mui-disabled': { color: '#7A7A7A' },
                                    '& .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: '#E6F4FF !important',
                                    },
                                }}
                                renderItem={(item) => <PaginationRenderIcon item={item} />}
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', mt: '20px' }}>
                    <Button
                        variant="outlined"
                        sx={{ borderRadius: '8px', height: '44px', width: '91px', fontWeight: 400, color: '#727272', borderColor: '#727272' }}
                        onClick={handleClose}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: '8px', height: '44px', fontWeight: 400 }}
                        disabled={selectedCount === 0 || deleting}
                        onClick={() => setConfirmOpen(true)}
                    >
                        {deleting ? 'Deleting...' : `Delete${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                    </Button>
                </Box>
            </Box>

            {confirmOpen && (
                <DeleteDialog
                    open
                    title="Bulk Delete Dispositions"
                    content={`This will delete ${selectedCount} selected disposition(s) permanently. You cannot undo this action.`}
                    onClose={() => setConfirmOpen(false)}
                    deleteFunction={handleDelete}
                />
            )}
        </OZDialog>
    );
};

export default BulkDeleteDispositionDialog;
